import { type ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $recipientListStore, $recipientRecentStore } from '@lib/store';
import { cn, PaletteUtils } from '@shared/utils';
import { type RecipientResponse, SortType, TransferRecipientTypeEnum } from '@shared/api';
import { SelectRecipient } from './selectRecipient';
import { RecentRecipient } from './recentRecipient';
import { RecipientDetails } from './recipientDetails';
import { View } from '@widgets';
import { ErrorMessage, Radio, Text } from '@shared/widgets';

const RecipientType = {
  Existing: 'Existing Recipient',
  New: 'New Recipient',
} as const;
type RecipientType = (typeof RecipientType)[keyof typeof RecipientType];

const RECIPIENT_TYPE_OPTIONS = Object.entries(RecipientType).map(([, text]) => ({ text, value: text }));

interface RecipientSelectBlockProps {
  className?: string;
  type: TransferRecipientTypeEnum;
  isInternalAccount?: boolean;
  value?: RecipientResponse | null;
  onChange: (recipient?: RecipientResponse | null) => void;
  error?: ReactNode;
  showRecipientDetails: boolean;
  onToggleRecipientDetails: () => void;
}

export const RecipientSelectBlock = (props: RecipientSelectBlockProps) => {
  const [searchParams] = useSearchParams();
  const recipientId = searchParams.get('recipientId');

  const recipientListStore = useUnit($recipientListStore.store);
  const recipientRecentStore = useUnit($recipientRecentStore.store);

  const [recipientType, setRecipientType] = useState<RecipientType>(RecipientType.Existing);
  const [searchValue, setSearchValue] = useState<string>('');

  const { setValue } = useFormContext();
  const { t } = useTranslation('payments');

  useEffect(() => {
    $recipientRecentStore.events.getRecent({ Type: props.type, isInternalAccount: !!props.isInternalAccount });
    return () => $recipientRecentStore.events.clear();
  }, [props.isInternalAccount, props.type]);

  useEffect(() => {
    $recipientListStore.events.getRecipients({
      SearchQuery: searchValue,
      isInternalAccount: !!props.isInternalAccount,
      Type: props.type,
      SortType: SortType.Asc,
      ReturnTotalCount: true,
      Count: 999,
    });
    return () => $recipientListStore.events.clear();
  }, [searchValue, props.isInternalAccount, props.type]);

  useEffect(() => {
    if (!recipientId) return;
    const recipient = recipientListStore.data.data.find((recipient) => recipient.id === recipientId);
    if (recipient) props.onChange(recipient);
  }, [recipientId, recipientListStore.data.data]);

  return (
    <div className={cn('flex flex-col gap-2', props.className)}>
      <Radio
        orientation='horizontal'
        options={RECIPIENT_TYPE_OPTIONS}
        value={recipientType}
        onChange={(value) => {
          const type = value as RecipientType;
          setRecipientType(type);
          switch (type) {
            case RecipientType.Existing:
              props.onChange(undefined);
              setValue('currency', undefined);
              break;
            case RecipientType.New:
              props.onChange(null);
              break;
          }
        }}
      />
      {recipientType === RecipientType.Existing && (
        <>
          <div>
            <SelectRecipient
              label={t('recipient.placeholders.transferTo')}
              loading={recipientListStore.loading}
              items={recipientListStore.data.data}
              value={props.value}
              onChange={props.onChange}
              searchValue={searchValue}
              onSearch={setSearchValue}
            />
            <ErrorMessage text={props.error} />
          </div>
          {!props.value && (
            <View
              loading={recipientRecentStore.loading}
              error={recipientRecentStore.error}
              onRefresh={() => $recipientRecentStore.events.getRecent({ Type: props.type, isInternalAccount: !!props.isInternalAccount })}
            >
              {recipientRecentStore.data.length > 0 && (
                <div className='flex flex-col'>
                  <Text className={cn('my-3', PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{t('recipient.title')}</Text>
                  <div className='flex flex-row overflow-x-auto'>
                    {recipientRecentStore.data.map((recipient, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          'mr-2 flex cursor-pointer rounded-sm px-2 py-3',
                          PaletteUtils.isInsigniaPalette && ['bg-ice dark:bg-eerie-black', 'hover:bg-ghost-white dark:hover:bg-transport-black'],
                          PaletteUtils.isVcfcPalette && ['bg-ghost-white dark:bg-light/5', 'hover:bg-gainsborough dark:hover:bg-light/20'],
                        )}
                        onClick={() => props.onChange(recipient)}
                      >
                        <RecentRecipient recipient={recipient} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </View>
          )}
          {props.value && (
            <RecipientDetails recipient={props.value} onToggle={props.onToggleRecipientDetails} isShowRecipientDetails={props.showRecipientDetails} />
          )}
        </>
      )}
    </div>
  );
};
